import { useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { connect } from 'react-redux';
import * as tracking from 'dibs-tracking';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import classNames from 'classnames';

// components
import { PdpRespPreviewMessageBanner } from '../PdpRespPreviewMessageBanner/PdpRespPreviewMessageBanner';
import { PdpRespPrintHeader } from '../PdpRespPrintHeader/PdpRespPrintHeader';
import { PdpRespSimilarItemsLanding } from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import { PdpRespBreadcrumbs } from '../PdpRespBreadcrumbs/PdpRespBreadcrumbs';
import { PdpRespHeroCarousel } from '../PdpRespHeroCarousel/PdpRespHeroCarousel';
import { PdpRespHeroCarouselPrint } from '../PdpRespHeroCarousel/PdpRespHeroCarouselPrint';
import { PdpRespTitle } from '../PdpRespTitle/PdpRespTitle';
import { PdpSharedSidebarFavorites } from '../PdpSharedSidebarFavorites/PdpSharedSidebarFavorites';
import { PriceDisplay } from 'dibs-buyer-price';
import { PdpRespSidebarButtons } from '../PdpRespSidebarButtons/PdpRespSidebarButtons';
import { PdpRespSidebarBlurbs } from '../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs';
import { PdpRespAboutModule } from '../PdpRespAboutModule/PdpRespAboutModule';
import { PdpRespShippingReturns } from '../PdpRespDetails/PdpRespShippingReturns';
import { PdpRespBuyerAssuranceBox } from '../PdpRespBuyerAssuranceBox/PdpRespBuyerAssuranceBox';
import { PdpRespStorytellingModule } from '../PdpRespStorytellingModule/PdpRespStorytellingModule';
import { PdpRespSidebarSellerInfo } from '../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfo';
import { PdpRespSidebarContact } from '../PdpRespSidebarContact/PdpRespSidebarContact';
import { PdpRespSimilarItemsFooter } from '../PdpRespSimilarItems/PdpRespSimilarItemsFooter';
import { PdpRespDealerDetails } from '../PdpRespDealerDetails/PdpRespDealerDetails';
import { PdpSharedQuantitySelect } from '../PdpSharedButtons/PdpSharedQuantitySelect';
import { PdpSharedReminderLoader } from '../PdpSharedReminder/PdpSharedReminderLoader';
import { PdpSharedRecentHistoryTracker } from '../PdpSharedRecentHistoryTracker/PdpSharedRecentHistoryTracker';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import { SharedRecentlyViewedItems } from '../../shared/SharedRecentlyViewedItems/SharedRecentlyViewedItems';
import { PdpSharedRestrictedMaterialsLoader } from '../PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterialsLoader';
import { PdpRespQrCode } from '../PdpRespQrCode/PdpRespQrCode';
import { PdpRespSwatchSelector } from '../PdpRespSwatchSelector/PdpRespSwatchSelector';
import { PdpSharedLeadTime } from '../PdpSharedLeadTime/PdpSharedLeadTime';
import { PdpRespExitModalLazy } from '../PdpRespExitModal/PdpRespExitModalLazy';
import { PdpSharedFrameIncludedLazy } from '../PdpSharedFrameIncluded/PdpSharedFrameIncludedLazy';
import { PdpRespDealerChatLoader } from '../PdpRespDealerChat/PdpRespDealerChatLoader';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { PdpSharedEditorialArticles } from '../PdpSharedEditorialArticles/PdpSharedEditorialArticles';

import { PageLayout } from 'dibs-elements/exports/PageLayout';
import { PdpRespTopModule } from '../PdpRespSoldRedesign/PdpRespTopModule/PdpRespTopModule';
import { PdpRespTopYMAL } from '../PdpRespSoldRedesign/PdpRespTopYMAL/PdpRespTopYMAL';
import { PdpRespDetailsTabs } from '../PdpRespSoldRedesign/PdpRespDetailsTabs/PdpRespDetailsTabs';
import { PdpRespSellerItems } from '../PdpRespSoldRedesign/PdpRespSellerItems/PdpRespSellerItems';

// callouts
import { NOTIFICATION_KEYS as SHARED_NOTIFICATION_KEYS } from '../PdpSharedNotifications/pdpSharedNotificationConstants';
import { SharedNotificationsWrapper } from '../../shared/SharedNotificationsWrapper';
import { PdpSharedNotificationMultiplePdpVisitsModalLoader } from '../PdpSharedNotifications/PdpSharedNotificationMultiplePdpVisitsModal/PdpSharedNotificationMultiplePdpVisitsModalLoader';
import { getSelectedSku } from '../helpers/getSelectedSku';

// helpers
import classnames from 'classnames';
import { useContactDealerAutoModal } from 'dibs-buyer-layout/exports/contactDealerLauncher';
import { trackPdpViewMoreCtaClick } from 'dibs-recent-history/src/recentHistoryTracking';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { trackAbTestEvent } from 'dibs-tracking';

// context
import { PdpSharedStorytellingTrackingContextProvider } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking';

// styles
import styles from './PdpRespContainer.scss';

/* eslint-disable react/prop-types */
// disable for function as child of PdpRespNotificationsWrapper

export function PdpRespContainerComponent(props) {
    useEffect(() => {
        const makeOfferTrackingData = getAbTestTrackingData(
            document.cookie,
            'REQUIRED_MESSAGE_WITH_OFFER'
        );
        if (makeOfferTrackingData?.testGroup) {
            tracking.trackAbTestEvent(makeOfferTrackingData, 'PDP view');
        }
    }, []);

    const {
        printWithPrice,
        currency,
        viewer,
        user,
        isPdpRespPreviewPage,
        item,
        displayInfo,
        similarLandingItems,
        shipmentQuote,
        shipment,
        relayVariables,
        relay,
        selectedSkuId,
        onSkuChange,
    } = props;
    const { isSoldPdpRedesign } = useContext(ServerVarsContext);
    const { isMultiSku, itemId, userId, isClient, isTrade, fetchUser, isVip } = relayVariables;
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const isNewAndCustom = item?.itemType === 'NEW_MADE_TO_ORDER';
    const selectedSkuLeadTime = selectedSku?.skuLeadTime;
    const isListingUnlisted = item?.isListingUnlisted;
    const isSellerAccessible = item?.seller?.isAccessible;
    const isSoldPdp = item?.isSold;

    useEffect(() => {
        if (isSoldPdp) {
            const trackingData = getAbTestTrackingData(document.cookie, 'SOLD_PDP_REDESIGN');
            trackAbTestEvent(trackingData, 'pdp_pageview');
        }
    }, [isSoldPdp]);

    useContactDealerAutoModal({
        itemId,
        selectedSkuId,
        userId,
        environment: relay.environment,
        currency,
    });

    const scrollToRef = useRef(null);
    const scrollToRefRedesign = useRef(null);

    return (
        <div>
            {/* Preview Page Message Banner */}
            {isPdpRespPreviewPage && <PdpRespPreviewMessageBanner />}

            {/* Print Header */}
            {isClient && <PdpRespPrintHeader itemId={itemId} className={styles.printShow} />}

            {/* Similar Items */}
            <div className={styles.printHide}>
                {isSoldPdpRedesign ? (
                    <PageLayout setViewportBackground setOffset>
                        <PdpRespTopModule item={item} viewer={viewer} user={user} />
                        <div className={styles.devider} />
                        <PdpRespTopYMAL
                            similarItems={similarLandingItems}
                            scrollToRef={scrollToRefRedesign}
                        />
                    </PageLayout>
                ) : (
                    <PdpRespSimilarItemsLanding
                        scrollToRef={scrollToRef}
                        item={item}
                        similarItems={similarLandingItems}
                        viewer={viewer}
                        user={user}
                    />
                )}
            </div>

            {/* Notification reminder loader */}
            <PdpSharedReminderLoader
                relayVariables={{
                    userId,
                    fetchUser,
                    itemId,
                    isClient,
                }}
                environment={relay.environment}
                load={!!user}
            />

            {/* PdpBreadcrumbs */}
            <div className={styles.printHide}>
                <PdpRespBreadcrumbs item={item} />
            </div>

            <div className="rowFlex">
                <div
                    className={classnames(styles.topContainer, {
                        'colXs12 colLg7 colXl8': !isSoldPdpRedesign,
                        'colXs12 colLg6 colLgOffset1 colXl6 colXlOffset1': isSoldPdpRedesign,
                    })}
                >
                    {/* Image Carousel */}
                    <div className={styles.printHide}>
                        <PdpRespHeroCarousel
                            itemTitle={item.title || ''}
                            itemImage={item.firstPhotoWebPath || ''}
                            item={item}
                            selectedSkuId={selectedSkuId}
                            user={user}
                            environment={relay.environment}
                            relayVariables={{
                                userId,
                                fetchUser,
                                itemId,
                                isClient,
                                isTrade,
                            }}
                            viewer={viewer || null}
                            disableZoomModal={isPdpRespPreviewPage}
                            disableRequestMoreImagesModal={isPdpRespPreviewPage}
                            hideBubbles={isSoldPdpRedesign}
                        />
                    </div>
                    {/* Stripped Image Carousel (print view) */}
                    {isClient && (
                        <div className={styles.printShow}>
                            <PdpRespHeroCarouselPrint item={item} selectedSkuId={selectedSkuId} />
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div
                    className={classnames(styles.topContainer, {
                        'colXs12 colLg5 colXl4': !isSoldPdpRedesign,
                        'colXs12 colLg4 colXl4': isSoldPdpRedesign,
                    })}
                >
                    <div
                        className={classnames(styles.sidebarContainer, {
                            [styles.isSoldRedesign]: isSoldPdpRedesign,
                        })}
                    >
                        <div className="rowFlex">
                            {/* Title */}
                            <div className="colXs12 colLg10">
                                <PdpRespTitle item={item} />
                            </div>
                            {/* Favorite / Folder */}
                            {viewer.isFavoritesEnabled && (
                                <div className={`${styles.printHide} colXs2`}>
                                    <PdpSharedSidebarFavorites
                                        environment={relay.environment}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                        itemTitle={item.title || ''}
                                        itemImage={item.firstPhotoWebPath || ''}
                                        itemId={itemId}
                                        isClient={isClient}
                                        userId={userId}
                                        wrapperClass={styles.favoritesWrapper}
                                    />
                                </div>
                            )}
                        </div>

                        <div
                            className={classnames({
                                [styles.printHide]: !printWithPrice,
                            })}
                        >
                            <div className={styles.priceWithAccessoryInlineContainer}>
                                <PriceDisplay
                                    currency={currency}
                                    item={item}
                                    useSoldIcon
                                    sku={isMultiSku ? selectedSku : null}
                                />
                                {isClient && <PdpSharedFrameIncludedLazy item={item} />}
                            </div>
                            {isSellerAccessible && (
                                <div className={styles.printHide}>
                                    <PdpSharedPromoCode
                                        displayInfo={displayInfo}
                                        itemId={itemId}
                                        user={user}
                                        userId={userId}
                                        excludeShippingPromos
                                    />
                                </div>
                            )}
                        </div>
                        {isMultiSku && (
                            <PdpRespSwatchSelector
                                currency={currency}
                                isClient={isClient}
                                item={item}
                                user={user}
                                selectedSkuId={selectedSkuId}
                                onClick={skuId => {
                                    onSkuChange(skuId);
                                }}
                                showSwatchDescriptionOnHover
                            />
                        )}
                        {isNewAndCustom && selectedSkuLeadTime && !isListingUnlisted && (
                            <div className={styles.leadTimeContainer}>
                                <PdpSharedLeadTime
                                    leadTime={selectedSkuLeadTime}
                                    valueClassName={styles.value}
                                />
                            </div>
                        )}
                        {/* Item Ctas */}
                        {isClient && (
                            <div className={`${styles.printHide} ${styles.itemCtasWrapper}`}>
                                <PdpSharedQuantitySelect item={item} selectedSkuId={selectedSkuId}>
                                    {function quantitySelection(quantitySelected) {
                                        return (
                                            <PdpRespSidebarButtons
                                                viewer={viewer}
                                                currency={currency}
                                                relayVariables={{
                                                    userId,
                                                    isMultiSku,
                                                    itemId,
                                                }}
                                                isClient={isClient}
                                                user={user}
                                                item={item}
                                                displayInfo={displayInfo}
                                                quantitySelected={quantitySelected}
                                                skuId={selectedSkuId}
                                            />
                                        );
                                    }}
                                </PdpSharedQuantitySelect>
                            </div>
                        )}

                        {/* Restricted Materials */}
                        {isClient && <PdpSharedRestrictedMaterialsLoader viewer={viewer} />}

                        {/* Blurbs */}
                        {isSellerAccessible && (
                            <PdpRespSidebarBlurbs
                                currency={currency}
                                relayVariables={{
                                    userId,
                                    itemId,
                                    isTrade,
                                    isVip,
                                    isClient,
                                }}
                                viewer={viewer}
                                item={item}
                                user={user}
                                displayInfo={displayInfo}
                                shipmentQuote={shipmentQuote}
                                shipment={shipment}
                                sku={isMultiSku ? selectedSku : null}
                                showLeadTimeBlurb={!isNewAndCustom && !isMultiSku}
                            />
                        )}
                        {isClient && isSellerAccessible && (
                            <div className={styles.printHide}>
                                <div className={styles.sidebarSection}>
                                    {/* Questions about this piece? / Contact Dealer */}
                                    <PdpRespSidebarContact
                                        displayInfo={displayInfo}
                                        item={item}
                                        selectedSkuId={selectedSkuId}
                                        user={user}
                                        viewer={viewer}
                                        itemId={itemId}
                                        userId={userId}
                                    />

                                    {/* Free Shipping Seller Stats Variant*/}
                                    <div className={styles.sellerInfoContainer}>
                                        <PdpRespSidebarSellerInfo
                                            seller={item.seller}
                                            viewer={viewer}
                                            relay={relay}
                                            user={user}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <PdpRespQrCode className={styles.printShow} itemId={itemId} />
                    </div>
                </div>
            </div>

            {isClient && isSoldPdpRedesign && (
                <div className={styles.printShow}>
                    {/* About */}
                    <PdpRespAboutModule
                        isClient={isClient}
                        item={item}
                        user={user}
                        currency={currency}
                        selectedSkuId={selectedSkuId}
                        viewer={viewer}
                    />

                    {/* Shipping and Returns */}
                    <PdpRespShippingReturns
                        currency={currency}
                        relayVariables={{
                            userId,
                            itemId,
                            isClient,
                        }}
                        viewer={viewer}
                        user={user}
                        item={item}
                        shipmentQuote={shipmentQuote}
                        shipment={shipment}
                    />
                </div>
            )}

            {isSoldPdpRedesign ? (
                <div className={styles.printHide}>
                    <PdpRespDetailsTabs
                        selectedSkuId={selectedSkuId}
                        viewer={viewer}
                        user={user}
                        item={item}
                    />
                    {/* Pdp Dealer Chat */}
                    {isClient && isSellerAccessible && <PdpRespDealerChatLoader viewer={viewer} />}

                    <PdpRespSellerItems viewer={viewer} user={user} item={item} />
                </div>
            ) : (
                <>
                    <div className={styles.details}>
                        {/* About */}
                        <PdpRespAboutModule
                            isClient={isClient}
                            item={item}
                            user={user}
                            currency={currency}
                            selectedSkuId={selectedSkuId}
                            viewer={viewer}
                        />

                        {/* Pdp Dealer Chat */}
                        {isClient && isSellerAccessible && (
                            <PdpRespDealerChatLoader viewer={viewer} />
                        )}
                        {/* Shipping and Returns */}
                        <PdpRespShippingReturns
                            currency={currency}
                            relayVariables={{
                                userId,
                                itemId,
                                isClient,
                            }}
                            viewer={viewer}
                            user={user}
                            item={item}
                            shipmentQuote={shipmentQuote}
                            shipment={shipment}
                        />

                        <PdpSharedStorytellingTrackingContextProvider>
                            {/* Storytelling Module */}
                            <div
                                className={classNames(styles.printHide, {
                                    [styles.storyTellingModulePadding]: !isSellerAccessible,
                                })}
                            >
                                <PdpRespStorytellingModule item={item} />
                            </div>
                        </PdpSharedStorytellingTrackingContextProvider>
                    </div>

                    {/* Contact Dealer Details */}
                    <div className={styles.printHide}>
                        <PdpRespDealerDetails
                            user={user}
                            item={item}
                            sku={isMultiSku ? selectedSku : null}
                            relay={relay}
                            viewer={viewer}
                            currency={currency}
                            isClient={isClient}
                        />
                    </div>
                </>
            )}

            {/* You May Also Like */}
            <div ref={scrollToRefRedesign} />
            <div className={styles.printHide}>
                <PdpRespSimilarItemsFooter
                    currency={currency}
                    viewer={viewer}
                    user={user}
                    item={item}
                    scrollToRef={scrollToRef}
                    relayVariables={relayVariables}
                />
            </div>

            <PdpSharedEditorialArticles item={item} />
            {/* Recently Viewed Items */}
            <div className={`${styles.printHide} ${styles.recentlyViewed}`}>
                <SharedRecentlyViewedItems
                    itemId={item.serviceId}
                    isClient={isClient}
                    user={user}
                    onViewMoreCtaClick={trackPdpViewMoreCtaClick}
                />
            </div>
            {/* Photos - todo (INTL-70: PDP follow on) */}
            {isClient && !viewer.disablePdpExitModal && !userId && (
                <PdpRespExitModalLazy item={item} />
            )}
            <SharedNotificationsWrapper type={SHARED_NOTIFICATION_KEYS.MULTI_PDP_VISITS}>
                {({ isEnabled }) => (
                    <PdpSharedNotificationMultiplePdpVisitsModalLoader
                        isEnabled={viewer.isFavoritesEnabled && isEnabled && !isPdpRespPreviewPage}
                        itemId={itemId}
                        environment={relay.environment}
                    />
                )}
            </SharedNotificationsWrapper>

            {/* Recent History */}
            {!isPdpRespPreviewPage && <PdpSharedRecentHistoryTracker item={item} />}

            {/* Buyer Assurance Box */}
            <div className={styles.printHide}>
                <PdpRespBuyerAssuranceBox user={user} />
            </div>

            {/* More Ways To Browse */}
            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    viewer={viewer}
                    item={item}
                    links={item.internalBuyLinks}
                    itemSearch={null}
                    onClick={() => {
                        tracking.trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                />
            </div>
        </div>
    );
}

PdpRespContainerComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        isMultiSku: PropTypes.bool.isRequired,
        isTrade: PropTypes.bool,
        fetchUser: PropTypes.bool.isRequired,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool.isRequired,
        skuId: PropTypes.string,
    }).isRequired,
    onSkuChange: PropTypes.func.isRequired,
    relay: PropTypes.object.isRequired,

    // relay response data
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    displayInfo: PropTypes.object,
    similarLandingItems: PropTypes.object,
    shipmentQuote: PropTypes.object,

    // from redux
    printWithPrice: PropTypes.bool.isRequired,
    openTradeAskSellerTooltip: PropTypes.func,
};

function mapStateToProps({ pdpPrint, pdpPreviewPage }) {
    return {
        printWithPrice: pdpPrint.printWithPrice,
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
    };
}

export const PdpRespContainer = createFragmentContainer(
    connect(mapStateToProps)(PdpRespContainerComponent),
    {
        viewer: graphql`
            fragment PdpRespContainer_viewer on Viewer {
                ...SharedRespLinksBlock_viewer
                ...PdpRespSidebarBlurbs_viewer
                ...PdpRespSidebarButtons_viewer
                ...PdpRespShippingReturns_viewer
                ...PdpRespSimilarItemsFooter_viewer
                ...PdpRespHeroCarousel_viewer @include(if: $fetchPdp)
                ...PdpRespDealerDetails_viewer
                ...PdpRespSidebarContact_viewer
                ...PdpRespSidebarSellerInfo_viewer
                ...PdpRespDealerChatLoader_viewer @include(if: $isClient)
                ...PdpRespAboutModule_viewer
                ...PdpSharedRestrictedMaterialsLoader_viewer
                ... @include(if: $fetchPdp) {
                    isFavoritesEnabled: isEnabledByLocale(featureName: "FAVORITES")
                    disablePdpExitModal: isEnabled(key: "disablePdpExitModal")
                    isAboutTheMakerEnabled: isEnabledByLocale(featureName: "ABOUT_THE_MAKER")
                }

                ...PdpRespTopModule_viewer
                ...PdpRespDetailsTabs_viewer
                ...PdpRespSellerItems_viewer
            }
        `,
        user: graphql`
            fragment PdpRespContainer_user on User {
                ...PdpRespSimilarItemsFooter_user
                ...PdpRespSidebarButtons_user
                ...PdpRespSidebarBlurbs_user
                ...PdpRespShippingReturns_user
                ...PdpRespBuyerAssuranceBox_user
                ...PdpRespSidebarContact_user
                ...PdpRespHeroCarousel_user
                ...PdpRespDealerDetails_user
                ...PdpRespSidebarSellerInfo_user
                ...PdpSharedPromoCode_user
                ...PdpRespSwatchSelector_user @include(if: $isMultiSku)
                ...SharedRecentlyViewedItems_user
                ...PdpRespAboutModule_user

                ...PdpRespTopModule_user
                ...PdpRespDetailsTabs_user
                ...PdpRespSellerItems_user
            }
        `,
        item: graphql`
            fragment PdpRespContainer_item on Item {
                serviceId
                firstPhotoWebPath(size: small)
                title
                defaultSkuId
                itemType
                isListingUnlisted
                isSold
                ...PdpSharedEditorialArticles_item
                ...PdpRespStorytellingModule_item
                ...PdpRespExitModal_item
                ...PdpRespSimilarItemsLanding_item
                ...PdpRespBreadcrumbs_item
                ...PdpRespHeroCarouselPrint_item
                ...PdpRespTitle_item
                ...PdpRespSidebarButtons_item
                ...PdpRespSidebarBlurbs_item
                ...PdpRespShippingReturns_item
                ...PdpRespSidebarContact_item
                ...PdpRespSimilarItemsFooter_item
                ...PdpSharedQuantitySelect_item
                ...PdpSharedRecentHistoryTracker_item
                ...SharedRespLinksBlock_item
                ...PriceDisplay_item
                    @arguments(hasUserId: $fetchUser, userId: $userId, isTrade: $isTrade)
                ...PdpRespSwatchSelector_item @include(if: $isMultiSku)
                ...PdpSharedFrameIncluded_item
                ...PdpRespDealerDetails_item
                ...PdpRespAboutModule_item

                ...PdpRespTopModule_item
                ...PdpRespDetailsTabs_item
                ...PdpRespSellerItems_item

                ... @include(if: $fetchPdp) {
                    seller {
                        isAccessible
                        ...PdpRespSidebarSellerInfo_seller
                    }
                    ...PdpRespHeroCarousel_item
                    internalBuyLinks {
                        ...SharedRespLinksBlock_links
                    }
                }
                skus {
                    ...PdpRespDealerDetails_sku @include(if: $isMultiSku)
                    serviceId
                    skuLeadTime: leadTime(pageType: PDP)
                    ...PriceDisplay_sku
                        @include(if: $isMultiSku)
                        @arguments(
                            hasUserId: $fetchUser
                            hasSkuId: true
                            userId: $userId
                            isTrade: $isTrade
                        )
                    ...PdpRespSidebarBlurbs_sku @include(if: $isMultiSku)
                }
            }
        `,
        displayInfo: graphql`
            fragment PdpRespContainer_displayInfo on ItemDisplayInfo {
                ...PdpRespSidebarButtons_displayInfo
                ...PdpRespSidebarContact_displayInfo
                ...PdpSharedPromoCode_displayInfo
                ...PdpRespSidebarBlurbs_displayInfo
            }
        `,
        similarLandingItems: graphql`
            fragment PdpRespContainer_similarLandingItems on moreLikeThisItemConnection {
                ...PdpRespSimilarItemsLanding_similarItems

                ...PdpRespTopYMAL_similarItems
            }
        `,
        shipmentQuote: graphql`
            fragment PdpRespContainer_shipmentQuote on ShipmentQuote {
                ...PdpRespSidebarBlurbs_shipmentQuote
                ...PdpRespShippingReturns_shipmentQuote
            }
        `,
        shipment: graphql`
            fragment PdpRespContainer_shipment on Shipment {
                ...PdpRespSidebarBlurbs_shipment
                ...PdpRespShippingReturns_shipment
            }
        `,
    }
);
